import * as debounce from 'lodash.debounce'
import * as loadjs from 'loadjs'
import { lazyTimeout } from '../constants'
import { ExperimentFlag } from '../enums/ExperimentFlag'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import { Device, WindowSize } from '../helpers'
import { Scroll } from '../helpers/Scroll'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
import SlotRepository from '../slots/SlotRepository'

export const TRIGGER_SUFFIX = 'waypoint'
export const DEFAULT_MOBILE_OFFSET = 2400
export const DEFAULT_DESKTOP_OFFSET = 1600

const cleanupMultiplier = 12
const cleanupHeight = WindowSize.height * cleanupMultiplier
export let maxVelocity

export default class Lazyable {
  // These statics let us debounce bid requests from lazy slots.
  static lazySlotsToRequest: Slot[] = []
  static debounceRequestBids: Function
  static disableContentWaypoints: boolean = false
  static contentDisabledUntil: number | null = null

  // remove ad after it's 12 window-heights above the viewPort.
  static cleanupObserver = new IntersectionObserver(cleanupCallback, {
    rootMargin: `${cleanupHeight}px 0px 0px 0px`
  })

  static lazyLoadObservers: IntersectionObserver[] = []

  rendered = false

  constructor(readonly slot: Slot, private prebidFacade: PrebidFacade) {
    slot.lazyable = this
    this.createWaypoint()

    // Set up the static Lazyable.debounceRequestBids if it doesn't already exist
    if (!Lazyable.debounceRequestBids) {
      Lazyable.debounceRequestBids = debounce(
        () => {
          // Let's find the largest timeout among the slots, and use that.
          const timeouts = Lazyable.lazySlotsToRequest.map(
            (slot) => slot.timeout || lazyTimeout()
          )
          const maxTimeout = Math.max(...timeouts)

          prebidFacade.requestBids(Lazyable.lazySlotsToRequest, maxTimeout)
          Lazyable.lazySlotsToRequest = []
        },
        100,
        { maxWait: 500 }
      )
    }
  }

  static get contentDisabledFor(): number {
    let disabledFor = 0
    if (Lazyable.contentDisabledUntil) {
      // calculate how much longer the content is disabled for
      disabledFor = Lazyable.contentDisabledUntil - Date.now()
      disabledFor = disabledFor > 0 ? disabledFor : 0
    }

    return disabledFor
  }

  static disableContentWaypointsFor(ms: number) {
    Lazyable.disableContentWaypoints = true
    Lazyable.contentDisabledUntil = Date.now() + ms
    setTimeout(() => {
      Lazyable.disableContentWaypoints = false
      Lazyable.contentDisabledUntil = null
    }, ms)
  }

  static setContentWaypointsDisable(disable: boolean) {
    Lazyable.disableContentWaypoints = disable
  }

  static renderLazyAds(htmlEl: HTMLElement): Array<Slot> {
    const slots: Array<Slot> = []
    const slotEls = Array.from(htmlEl.querySelectorAll('.adunit'))
    slotEls.map((slotEl) => {
      const id = slotEl.getAttribute('id')
      if (!id) {
        return
      }

      const slot = SlotRepository.getSlotById(id)

      if (!slot || !slot.lazyable) {
        return
      }

      if (Lazyable.renderAd(slot)) {
        slots.push(slot)
      }
    })

    return slots
  }

  static renderAd(slot: Slot): boolean {
    if (slot.lazyable.rendered) {
      return false
    }
    slot.lazyable.rendered = true
    Lazyable.lazySlotsToRequest.push(slot)
    Lazyable.debounceRequestBids()

    return true
  }

  createWaypoint(): void {
    const slot: Slot = this.slot

    const offset = slot.offset
      ? slot.offset
      : Device.isMobileOrTablet
      ? DEFAULT_MOBILE_OFFSET
      : DEFAULT_DESKTOP_OFFSET

    // Different slots have different offsets, so here we see if we've created an
    // IntersectionObserver for a particular offset value, and create it if it doesn't exist.
    let lazyLoadObserver = Lazyable.lazyLoadObservers[slot.offset]
    if (!lazyLoadObserver) {
      const options = {
        rootMargin: `0px 0px ${offset}px 0px`
      }

      lazyLoadObserver = new IntersectionObserver(lazyLoadCallback, options)
      Lazyable.lazyLoadObservers[offset] = lazyLoadObserver
    }

    requestAnimationFrame(() => {
      const observedEl = this.slot.wrapper
      lazyLoadObserver.observe(observedEl)
    })
  }
}

/**
 * IntersectionObserver callback that that lazy-loads ads as the user scrolls close enough to them.
 * @param entries
 * @param observer
 */
function lazyLoadCallback(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
): void {
  const velocity = Math.round(Scroll.delta)

  entries.forEach((entry) => {
    const slotId = entry.target.getAttribute('data-wrapper') || ''
    const slot = SlotRepository.getSlotById(slotId)

    if (
      !slot ||
      (slot.slotName === 'content' && Lazyable.disableContentWaypoints)
    ) {
      return
    }

    maxVelocity = getMaxVelocity(slot)
    // Max Velocity check should only apply to content slots
    const tooFast = slot.slotName === 'content' && velocity > maxVelocity

    if ((entry.isIntersecting || slot.sticky) && !tooFast) {
      observer.unobserve(entry.target)
      loadjs.ready(LoadJsBundle.nonLazyRequestsLoaded, () => {
        // Wait for NonLazy Ads to have loaded before requesting new bids.
        slot.setTargeting({ velocity, maxVelocity })
        Lazyable.renderAd(slot)

        // Setup cleanup observer
        if (!slot.sticky) {
          Lazyable.cleanupObserver.observe(slot.wrapper)
        }
      })
    }
  })
}

/**
 * IntersectionObserver callback that removes ads from the DOM if the user scrolls too far past it.
 * @param entries
 * @param observer
 */
function cleanupCallback(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver
): void {
  entries.forEach((entry) => {
    const slotId = entry.target.getAttribute('data-wrapper')
    const isAboveViewport = entry.boundingClientRect.bottom < 0

    // Sanity check. It helps one site (https://www.bucketlistly.blog/photos),
    // where Content-hints are used in a modal, work correctly.
    // Things might get weird when with modals / fixed positioning?
    const isAboveCleanupHeight =
      entry.boundingClientRect.bottom <= -cleanupHeight

    if (
      slotId &&
      isAboveViewport &&
      isAboveCleanupHeight &&
      entry.intersectionRatio === 0
    ) {
      observer.unobserve(entry.target)
      const slot = SlotRepository.getSlotById(slotId)
      if (slot) {
        slot.destroyGSlot()
        const wrapperDiv = slot.wrapper as HTMLDivElement
        wrapperDiv.style.visibility = 'hidden'
      }
    }
  })
}

function getMaxVelocity(slot: Slot): number {
  return (
    maxVelocity ||
    parseInt(
      slot.model.experiment.activate(ExperimentFlag.velocityCheck) || '0',
      10
    ) ||
    1000
  )
}

window.$mediavine.web.renderLazyAds = Lazyable.renderLazyAds
window.$mediavine.web.setContentWaypointDisable =
  Lazyable.setContentWaypointsDisable
window.$mediavine.web.disableContentWaypointsFor =
  Lazyable.disableContentWaypointsFor
